import { createModelToolStorageKey } from "@/pages/ModelTool/utils/createLocalStorageKey";

import { Tab, TabTarget } from "./tabTarget";
import { TabsState, emptyTabsState, nextScratchLabel } from "./tabsReducer";

const VERSION = 2;

export const tabsStorageKey = (accountId: string) =>
  createModelToolStorageKey(`tabs.v${VERSION}`, accountId);

/** Keys written by the pre-v2 implementation, read once to migrate and then left alone. */
const legacyTabsKey = (accountId: string) =>
  createModelToolStorageKey("openTabs", accountId);
const legacyActiveTabKey = (accountId: string) =>
  createModelToolStorageKey("currentTab", accountId);

type PersistedShape = {
  version: number;
  tabs: Tab[];
  activeTabId: string | null;
};

/**
 * Anything that fails validation is dropped rather than repaired. Persisted tabs are a convenience,
 * not data the user would miss, and a half-parsed tab produces a strip that renders blank rows.
 */
export function parseTabsState(raw: string | null): TabsState | null {
  if (!raw) return null;
  const parsed = tryParseJson(raw);
  if (!isRecord(parsed)) return null;
  if (parsed.version !== VERSION) return null;

  const tabs = Array.isArray(parsed.tabs)
    ? parsed.tabs.map(parseTab).filter((tab): tab is Tab => tab != null)
    : [];
  const activeTabId =
    typeof parsed.activeTabId === "string" &&
    tabs.some((tab) => tab.id === parsed.activeTabId)
      ? parsed.activeTabId
      : null;

  return { tabs, activeTabId };
}

export function serializeTabsState(state: TabsState): string {
  const payload: PersistedShape = {
    version: VERSION,
    tabs: state.tabs,
    activeTabId: state.activeTabId,
  };
  return JSON.stringify(payload);
}

function parseTab(value: unknown): Tab | null {
  if (!isRecord(value)) return null;
  if (typeof value.id !== "string" || value.id.length === 0) return null;
  const target = parseTarget(value.target);
  if (!target) return null;
  return {
    id: value.id,
    target,
    cachedLabel:
      typeof value.cachedLabel === "string" ? value.cachedLabel : undefined,
  };
}

function parseTarget(value: unknown): TabTarget | null {
  if (!isRecord(value)) return null;
  switch (value.kind) {
    case "model":
      return isNonEmptyString(value.modelId)
        ? {
            kind: "model",
            modelId: value.modelId,
            dashboardId: optionalString(value.dashboardId),
            tileId: optionalString(value.tileId),
          }
        : null;
    case "draft":
      return isNonEmptyString(value.draftId)
        ? { kind: "draft", draftId: value.draftId }
        : null;
    case "dashboard":
      return isNonEmptyString(value.dashboardId)
        ? { kind: "dashboard", dashboardId: value.dashboardId }
        : null;
    case "scratch":
      return { kind: "scratch" };
    default:
      return null;
  }
}

/**
 * The v1 shape: a flat bag of optional ids discriminated by a `type` string. `latestPreviewId` is
 * dropped — preview results only ever lived in memory, so a persisted id was always dangling.
 */
function migrateLegacyTab(value: unknown): Tab | null {
  if (!isRecord(value)) return null;
  if (typeof value.id !== "string" || value.id.length === 0) return null;

  const cachedLabel = optionalString(value.label);
  const modelId = optionalString(value.modelId);
  const dashboardId = optionalString(value.dashboardId);

  switch (value.type) {
    case "model":
      return modelId
        ? {
            id: value.id,
            target: {
              kind: "model",
              modelId,
              dashboardId,
              tileId: optionalString(value.tileId),
            },
            cachedLabel,
          }
        : null;
    case "draft":
      return modelId
        ? {
            id: value.id,
            target: { kind: "draft", draftId: modelId },
            cachedLabel,
          }
        : null;
    case "dashboard":
      return dashboardId
        ? {
            id: value.id,
            target: { kind: "dashboard", dashboardId },
            cachedLabel,
          }
        : null;
    case "empty":
      return { id: value.id, target: { kind: "scratch" }, cachedLabel };
    default:
      return null;
  }
}

export function migrateLegacyTabsState(
  rawTabs: string | null,
  rawActiveTabId: string | null,
): TabsState | null {
  if (!rawTabs) return null;
  const parsed = tryParseJson(rawTabs);
  if (!Array.isArray(parsed)) return null;

  const tabs: Tab[] = [];
  for (const value of parsed) {
    const tab = migrateLegacyTab(value);
    if (tab == null) continue;
    // v1 had no per-tab label for empty tabs, so they would all read "New tab" after migrating.
    tabs.push(
      tab.target.kind === "scratch" && !tab.cachedLabel
        ? { ...tab, cachedLabel: nextScratchLabel(tabs) }
        : tab,
    );
  }
  if (tabs.length === 0) return null;

  return {
    tabs,
    activeTabId: tabs.some((tab) => tab.id === rawActiveTabId)
      ? rawActiveTabId
      : null,
  };
}

/** Reads v2 state, falling back to a one-time migration of the v1 keys. */
export function loadTabsState(accountId: string): TabsState {
  const current = parseTabsState(safeGetItem(tabsStorageKey(accountId)));
  if (current) return current;

  const migrated = migrateLegacyTabsState(
    safeGetItem(legacyTabsKey(accountId)),
    safeGetItem(legacyActiveTabKey(accountId)),
  );
  if (migrated) {
    saveTabsState(accountId, migrated);
    return migrated;
  }

  return emptyTabsState;
}

export function saveTabsState(accountId: string, state: TabsState) {
  try {
    localStorage.setItem(tabsStorageKey(accountId), serializeTabsState(state));
  } catch {
    // Storage is a convenience here; a full or blocked store must not break the editor.
  }
}

function safeGetItem(key: string) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function tryParseJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.length > 0;

const optionalString = (value: unknown): string | undefined =>
  isNonEmptyString(value) ? value : undefined;
